import { useLayoutEffect, useMemo, useRef } from 'react';
import useState from './useState.js';
import useCreateVirtualScroller from './useCreateVirtualScroller.js';
import useStartStopVirtualScroller from './useStartStopVirtualScroller.js';
import useSetNewItemsOnItemsPropertyChange from './useSetNewItemsOnItemsPropertyChange.js';
import useValidateTableBodyItemsContainer from './useValidateTableBodyItemsContainer.js';
import useClassName from './useClassName.js';
import useStyle from './useStyle.js'; // When `items` property changes:
// * A new `items` property is supplied to the React component.
// * The React component re-renders itself.
// * `useSetNewItemsOnItemsPropertyChange()` hook is run.
// * `useSetNewItemsOnItemsPropertyChange()` hook detects that the `items` property
//   has changed and calls `VirtualScroller.setItems(items)`.
// * `VirtualScroller.setItems(items)` calls `VirtualScroller.setState()`.
// * `VirtualScroller.setState()` calls the `setState()` function.
// * The `setState()` function calls a setter from a `useState()` hook.
// * The React component re-renders itself the second time.

export default function useVirtualScroller(_ref) {
  var itemsProperty = _ref.items,
      tbody = _ref.tbody,
      readyToStart = _ref.readyToStart,
      styleProperty = _ref.style,
      classNameProperty = _ref.className,
      bypass = _ref.bypass,
      preserveScrollPosition = _ref.preserveScrollPosition,
      preserveScrollPositionOnPrependItems = _ref.preserveScrollPositionOnPrependItems,
      measureItemsBatchSize = _ref.measureItemsBatchSize,
      scrollableContainer = _ref.scrollableContainer,
      getScrollableContainer = _ref.getScrollableContainer,
      getColumnsCount = _ref.getColumnsCount,
      getItemId = _ref.getItemId,
      onItemFirstRender = _ref.onItemFirstRender,
      onItemInitialRender = _ref.onItemInitialRender,
      initialScrollPosition = _ref.initialScrollPosition,
      onScrollPositionChange = _ref.onScrollPositionChange,
      initialState = _ref.initialState,
      getInitialItemState = _ref.getInitialItemState,
      onStateChange = _ref.onStateChange,
      getEstimatedItemHeight = _ref.getEstimatedItemHeight,
      getEstimatedVisibleItemRowsCount = _ref.getEstimatedVisibleItemRowsCount,
      getEstimatedInterItemVerticalSpacing = _ref.getEstimatedInterItemVerticalSpacing;
  // A `ref` to the items container element.
  var itemsContainerRef = useRef(); // Use `hasMounted` flag to decide if `itemsContainerRef` value should be validated.

  var hasMounted = useRef(false);
  useLayoutEffect(function () {
    hasMounted.current = true;
  }, []); // Create a `VirtualScroller` instance.

  var virtualScroller = useCreateVirtualScroller({
    items: itemsProperty,
    getEstimatedItemHeight: getEstimatedItemHeight,
    getEstimatedVisibleItemRowsCount: getEstimatedVisibleItemRowsCount,
    getEstimatedInterItemVerticalSpacing: getEstimatedInterItemVerticalSpacing,
    bypass: bypass,
    // bypassBatchSize,
    onItemInitialRender: onItemInitialRender,
    // `onItemFirstRender(i)` is deprecated, use `onItemInitialRender(item)` instead.
    onItemFirstRender: onItemFirstRender,
    initialScrollPosition: initialScrollPosition,
    onScrollPositionChange: onScrollPositionChange,
    measureItemsBatchSize: measureItemsBatchSize,
    // `scrollableContainer` property is deprecated.
    // Use `getScrollableContainer()` property instead.
    scrollableContainer: scrollableContainer,
    getScrollableContainer: getScrollableContainer,
    getColumnsCount: getColumnsCount,
    getItemId: getItemId,
    initialState: initialState,
    getInitialItemState: getInitialItemState,
    onStateChange: onStateChange
  }, {
    getItemsContainerElement: function getItemsContainerElement() {
      // Validate that the developer has correctly passed the returned `itemsContainerRef` property
      // as the items container component's `ref`.
      //
      // `getItemsContainerElement()` function is called both before it has mounted
      // and after it has mounted. Only validate the ref's value after it has mounted.
      //
      if (!itemsContainerRef.current) {
        if (hasMounted.current) {
          throw new Error('[virtual-scroller] Did you forget to pass the returned `itemsContainerRef` property as the items container component\'s `ref`?');
        }
      } // Return the items container element.


      return itemsContainerRef.current;
    }
  }); // Only compute the initial state once.

  var _initialState = useMemo(function () {
    return virtualScroller.getInitialState();
  }, []); // Use React's `useState()` hook for managing `VirtualScroller`'s state lifecycle.
  // This way, React will re-render the component on every state update.


  var _useState = useState({
    initialState: _initialState,
    onRender: virtualScroller.onRender
  }),
      getState = _useState.getState,
      setState = _useState.setState,
      stateToRender = _useState.stateToRender; // Use custom (external) state storage in the `VirtualScroller`.


  useMemo(function () {
    virtualScroller.useState({
      getState: getState,
      setState: setState
    });
  }, []); // Start `VirtualScroller` on mount.
  // Stop `VirtualScroller` on unmount.

  useStartStopVirtualScroller(virtualScroller, {
    readyToStart: readyToStart
  }); // Calls `.setItems()` if `items` property has changed.

  useSetNewItemsOnItemsPropertyChange(itemsProperty, {
    virtualScroller: virtualScroller,
    // `preserveScrollPosition` property name is deprecated,
    // use `preserveScrollPositionOnPrependItems` property instead.
    preserveScrollPosition: preserveScrollPosition,
    preserveScrollPositionOnPrependItems: preserveScrollPositionOnPrependItems
  }); // A developer might "forget" to pass `itemsContainerComponent="tbody"` property
  // when using a `<tbody/>` as a container for list items.
  // This hook validates that the developer didn't "forget" to do that in such case.

  useValidateTableBodyItemsContainer({
    virtualScroller: virtualScroller,
    tbody: tbody
  });
  var className = useClassName(classNameProperty, {
    tbody: tbody
  });
  var style = useStyle(styleProperty, {
    tbody: tbody,
    state: stateToRender
  });
  return {
    state: stateToRender,
    style: style,
    className: className,
    itemsContainerRef: itemsContainerRef,
    virtualScroller: virtualScroller
  };
}
//# sourceMappingURL=useVirtualScroller.js.map